import { cn } from '@navioworks/ui';
import {
  Anchor,
  BarChart3,
  Banknote,
  Briefcase,
  CalendarDays,
  CheckCircle,
  ClipboardCheck,
  ClipboardList,
  CreditCard,
  FileCheck,
  FileText,
  Flag,
  FolderOpen,
  FolderTree,
  GitPullRequest,
  Calculator,
  LayoutDashboard,
  ListChecks,
  Mail,
  MapPin,
  MessageSquare,
  Package,
  PenTool,
  Scale,
  Ship,
  SlidersHorizontal,
  Users,
  Wrench,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  pillarForPath,
  pillarOrder,
  NAVIGATION_CONFIG,
  type Pillar,
  type NavItem,
} from '@/types/navigation';

import type { ComponentType } from 'react';

type IconComponent = ComponentType<{ className?: string }>;

// NavioWorks resolves icons by name from a map; same here so the config stays plain data.
const iconMap: Record<string, IconComponent> = {
  Anchor,
  BarChart3,
  Banknote,
  Briefcase,
  CalendarDays,
  Calculator,
  CheckCircle,
  ClipboardCheck,
  ClipboardList,
  CreditCard,
  FileCheck,
  FileText,
  Flag,
  FolderOpen,
  FolderTree,
  GitPullRequest,
  LayoutDashboard,
  ListChecks,
  Mail,
  MapPin,
  MessageSquare,
  Package,
  PenTool,
  Scale,
  Ship,
  SlidersHorizontal,
  Users,
  Wrench,
};

const buttonBase =
  'flex min-h-8 w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs font-medium transition-colors';
const activeClass = 'bg-muted text-foreground shadow-sm';
const idleClass = 'text-muted-foreground hover:bg-muted hover:text-foreground';

export function AppSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const activePillar = pillarForPath(location.pathname);
  const groups = NAVIGATION_CONFIG[activePillar].groups;

  const isActivePath = (path: string) =>
    path === '/'
      ? location.pathname === '/'
      : location.pathname === path || location.pathname.startsWith(`${path}/`);

  const goToPillar = (pillar: Pillar) =>
    navigate(NAVIGATION_CONFIG[pillar].groups[0].items[0].path);

  return (
    <aside className="hidden w-40 shrink-0 flex-col border-r border-border bg-background/95 md:flex">
      <nav className="border-b border-border p-1.5" aria-label="Primary pillars">
        <div className="space-y-1">
          {pillarOrder.map((pillar) => {
            const cfg = NAVIGATION_CONFIG[pillar];
            const Icon = iconMap[cfg.icon] ?? LayoutDashboard;
            const active = pillar === activePillar;
            return (
              <button
                key={pillar}
                type="button"
                onClick={() => goToPillar(pillar)}
                className={cn(buttonBase, active ? activeClass : idleClass)}
              >
                <Icon className="h-3.5 w-3.5 shrink-0" />
                <span className="min-w-0 truncate">{cfg.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {activePillar !== 'today' && (
        <nav className="min-h-0 flex-1 overflow-y-auto p-1.5" aria-label="Pillar domains">
          <div className="space-y-3">
            {groups.map((group) => (
              <div key={group.id}>
                {group.items.length === 1 && group.items[0]?.id === group.id ? (
                  <ModuleButton
                    module={group.items[0]}
                    active={isActivePath(group.items[0].path)}
                    onClick={() => navigate(group.items[0].path)}
                  />
                ) : (
                  <>
                    <div className="mb-1.5 px-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                      {group.label}
                    </div>
                    <div className="space-y-1">
                      {group.items.map((item) => (
                        <ModuleButton
                          key={item.id}
                          module={item}
                          active={isActivePath(item.path)}
                          onClick={() => navigate(item.path)}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </nav>
      )}

      {/* Product wordmark — which Navio app this is, pinned to the rail bottom. */}
      <div className="mt-auto border-t border-border px-3 py-3 text-base font-semibold tracking-tight text-foreground">
        NavioBuild
      </div>
    </aside>
  );
}

function ModuleButton({
  module,
  active,
  onClick,
}: {
  module: NavItem;
  active: boolean;
  onClick: () => void;
}) {
  const Icon = iconMap[module.icon] ?? FileText;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      className={cn(buttonBase, active ? activeClass : idleClass)}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" />
      <span className="min-w-0 truncate">{module.label}</span>
    </button>
  );
}
